import {flatten, get, groupBy, includes, map} from 'lodash';
import {setPlayingContestUsersSuccess} from "modules/actions/here_now_users";

import {
	changeStateAfterRound,
	changeStateInGame,
	changeStateOfGame,
	clearQuestions,
	getWagerSuccess,
	gotAnswerEvent,
	hideBonusPointsSwipeIt,
	hidePerfectModal,
	hidePoints,
	hidePointsWager,
	makeAnswer,
	recordQuestion,
	recordQuestions,
	removeAnswerEvent,
	setBonusPoints,
	setQuestionResult,
	setQuestionsResults,
	setTriviaFooterState,
	showBonusPointsSwipeIt,
	showPerfectModal,
	showPoints,
	showPointsWager,
	showQuestion,
	showSetQuestions
} from "modules/actions/trivia";
import * as ENUMS from "modules/utils/enums";
import {createReducer} from "redux-act";


type T_ID = number;

export interface ISetWagerAction {
	round_id?: number;
	wager: number
}

export interface IAnswerChoice {
	id: number,
	title: string,
	question_id?: number,
	round_id?: number,
	value?: string,
	percents?: number
}

export interface ITriviaQuestion {
	id: T_ID,
	title: string,
	answer_type: ENUMS.EnumTriviaAnswerType,
	fun_fact: string | null,
	answer_choices: IAnswerChoice[]
	status?: string
	end_date_time?: Date
	start_date_time?: Date
	position: number
	round_id: number
	is_last: boolean
	is_practice?: number
	time_to_results: number,
	time_to_fan_fact: number,
	time_to_bubbles_message: number
	levenshtein_percents?: number
}

export interface IPayloadSockets {
	questions: ITriviaQuestion[];
	round_id: number;
}

export interface ITriviaCorrectAnswer {
	id: T_ID,
	question_id: T_ID,
	value: string,
	percents: number,
	variants?: string[]
}

interface IAnswer {
	id: number,
	value: string
}

export interface IUserAnswer {
	question_id: number;
	answered_time: number;
	answers: IAnswer[];
	answer_id?: number;
	value?: string;
	is_modified?: boolean;
}

export interface IQuestionResultAction {
	question_id: T_ID;
	position: number;
	answer_state: ENUMS.EnumAnswerStates;
	points: number | null;
	round_id: number;
}

export interface ITriviaFooter {
	ticker: ENUMS.EnumFooterTickerState,
	right_side: ENUMS.EnumFooterRightSideState,
	tos: boolean,
	show_footer_msg: ENUMS.EnumFooterMsgState,
}

export interface ITriviaGame {
	questions: ITriviaQuestion[],
	answers: Record<string, ITriviaCorrectAnswer[]>,
	is_show_warning: boolean,
	is_show_fan_fact: boolean,
	is_loading: boolean,
	is_show_points_badge: boolean,
	is_show_points_wager_badge: boolean,
	is_show_perfect_round_modal: boolean,
	is_show_bonus_points_swipe_it: boolean,
	playing_contest_id: number,
	display_question: ITriviaQuestion,
	display_set_questions: ITriviaQuestion[],
	user_answers: Record<string, IUserAnswer>
	questions_result: {
		questions: Record<string, IQuestionResultAction>,
		wager: number,
	},
	bonus_points: number,
	wager: {
		wager_value: number,
		contest_points: number,
		is_fetched: boolean
	}
	footer_state: ITriviaFooter
	state_of_game: ENUMS.EnumTriviaStatesOfGame,
	state_in_game: ENUMS.EnumInGameState,
	state_after_round: ENUMS.EnumAfterRoundState
}

const defaultQuestion = {
	id: 0,
	title: '',
	answer_type: ENUMS.EnumTriviaAnswerType.binary,
	fun_fact: null,
	answer_choices: [],
	round_id: 0,
	position: 0,
	time_to_bubbles_message: 0,
	time_to_results: 0,
	time_to_fan_fact: 0,
	is_last: false
};

const defaultState: ITriviaGame = {
	questions: [],
	answers: {},
	is_show_warning: false,
	is_show_fan_fact: false,
	is_show_points_badge: false,
	is_show_points_wager_badge: false,
	playing_contest_id: 0,
	is_show_perfect_round_modal: false,
	is_loading: false,
	is_show_bonus_points_swipe_it: false,
	display_question: defaultQuestion,
	display_set_questions: [],
	questions_result: {
		questions: {},
		wager: 0
	},
	bonus_points: 0,
	wager: {
		wager_value: 0,
		is_fetched: false,
		contest_points: 0
	},
	user_answers: {},
	footer_state: {
		ticker: ENUMS.EnumFooterTickerState.pre_game,
		right_side: ENUMS.EnumFooterRightSideState.sponsor,
		tos: false,
		show_footer_msg: ENUMS.EnumFooterMsgState.none
	},
	state_of_game: ENUMS.EnumTriviaStatesOfGame.loading,
	state_in_game: ENUMS.EnumInGameState.unknown,
	state_after_round: ENUMS.EnumAfterRoundState.unknown,
};

export const trivia = createReducer<ITriviaGame>( {}, defaultState );

/**
 * Change State of the Trivia Game
 */
trivia
	.on(changeStateOfGame, (state, payload) => ( {
		...state,
		state_of_game: payload
	} ))
	.on( changeStateInGame, ( state, payload ) => ({
		...state,
		state_in_game: payload
	}) );

/**
 * Actions to manage UI flow of questions
 */
trivia
	.on(showQuestion, (state, payload) => {
		return {
			...state,
			display_question: payload
		}
	})
	.on(showSetQuestions, (state, payload) => {
		return {
			...state,
			display_set_questions: payload
		}
	})
;

/**
 * Record question to store it. Used for counting number of question
 */
trivia
	.on( recordQuestion, ( state, payload ) => {
		const is_exist_question = includes(map(state.questions, 'id'), payload.id);
		return is_exist_question ? state : {
			...state,
			questions: [
				...state.questions,
				payload
			]
		}
	} )
	.on( recordQuestions, ( state, payload ) => {
		return {
			...state,
			questions: payload
		}
	} );

/**
 * In case some error ability to clear all questions ans start again
 */
trivia
	.on( clearQuestions, ( state ) => ({
		...state,
		questions: defaultState.questions
	}) );


/**
 * Store answer
 */
trivia
	.on( makeAnswer, ( state, payload ) => ({
		...state,
		user_answers: {
			...state.user_answers,
			[payload.question_id]: {
				...payload,
				is_modified: !!get( state, `user_answers[${ payload.question_id }]`, false )
			}
		}
	}) )
	.on( gotAnswerEvent, ( state, payload ) => {
		// Todo Refactor to Many<AnswerEventTypes>
		// @ts-ignore
		const flattened_payload = flatten(payload);

		return {
			...state,
			answers: {
				...state.answers,
				...groupBy(flattened_payload, 'question_id')
			}
		}
	} )
	.on( removeAnswerEvent, ( state, payload ) => {
		return {
			...state,
			answers: {
				...state.answers,
				[payload]: []
			}
		}
	} )
	.on(setQuestionResult, (state, payload) => ( {
		...state,
		questions_result: {
			...state.questions_result,
			questions: {
				...state.questions_result.questions,
				[ payload.question_id ]: payload
			}
		}
	} ))
	.on(setQuestionsResults, (state, payload) => ( {
		...state,
		questions_result: payload
	} ));

/**
 * Points badge
 */
trivia
	.on(showPoints, (state) => ( {
		...state,
		is_show_points_badge: true
	} ))
	.on(hidePoints, (state) => ( {
		...state,
		is_show_points_badge: false
	} ))
	.on(showPointsWager, (state) => ( {
		...state,
		is_show_points_wager_badge: true
	} ))
	.on(hidePointsWager, (state) => ( {
		...state,
		is_show_points_wager_badge: false
	} ));

/**
 * TriviaFoterOptions
 */
trivia
	.on(setTriviaFooterState, (state, payload) => ( {
		...state,
		footer_state: {
			...state.footer_state,
			...payload
		}
	} ));

/**
 * Wager round state handlers
 */
trivia
	.on(changeStateAfterRound, (state, payload) => ( {
		...state,
		state_after_round: payload
	} ))

	.on(getWagerSuccess, (state, payload) => ( {
		...state,
		questions_result: {
			...state.questions_result,
			wager: payload.wager_value,
		},
		wager           : {
			...payload,
			is_fetched: true
		}
	} ))


/**
 * Perfect round state handlers
 */
trivia
	.on(showPerfectModal, (state) => ( {
		...state,
		is_show_perfect_round_modal: true
	} ))

	.on(hidePerfectModal, (state) => ( {
		...state,
		is_show_perfect_round_modal: false

	} ));

/**
 * Swipe it bonus points handler
 */
trivia
	.on(showBonusPointsSwipeIt, (state) => ( {
		...state,
		is_show_bonus_points_swipe_it: true
	} ))

	.on(hideBonusPointsSwipeIt, (state) => ( {
		...state,
		is_show_bonus_points_swipe_it: false

	} ))

	.on(setBonusPoints, (state, payload) => ( {
		...state,
		bonus_points: payload
	} ));


trivia.on(setPlayingContestUsersSuccess, (state, payload) => ( {
	...state,
	playing_contest_id: payload
} ))
